import { useQuery } from "@tanstack/react-query";
import { getTokens } from "../utils/auth";
import {
  getFeaturedPublicImagesApiPublicGalleryFeaturedGet,
  getPublicGalleryApiPublicGalleryGet,
  getPublicAlbumApiPublicAlbumsAlbumIdGet,
  getPublicAlbumsApiPublicAlbumsGet,
  getFeaturedPublicAlbumsApiPublicAlbumsFeaturedGet,
  getPublicAlbumImagesApiPublicAlbumsAlbumIdImagesGet,
} from "../api/generated";
import type { ImageResponse, AlbumResponse } from "../api/generated";
import { Gallery } from "../components/portfolio/GalleryCard";

/**
 * Transform API album data into gallery format
 * If no cover image, will fetch first image from gallery (handled in useFeaturedGalleries/usePublicGalleries)
 */
function transformAlbumsToGalleries(albums: AlbumResponse[]): Gallery[] {
  return albums.map((album) => ({
    id: album.id,
    title: album.title,
    coverImage: album.cover_image_url || undefined,
    thumbnail: album.cover_image_url || undefined,
    date: new Date(album.created_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    }),
    category: album.slug,
    imageCount: album.image_count || 0,
    description: album.description || undefined,
  }));
}

/**
 * Transform API image data into gallery format (legacy support)
 */
function transformImagesToGalleries(images: ImageResponse[]): Gallery[] {
  // Group images by category for now
  // In a real implementation, you might want to group by a specific gallery field
  const categoryGroups = images.reduce((acc, image) => {
    const category = image.category_id || "Uncategorized";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(image);
    return acc;
  }, {} as Record<string, ImageResponse[]>);

  // Convert each category group to a gallery
  return Object.entries(categoryGroups).map(([category, categoryImages]) => {
    const featuredImage =
      categoryImages.find((img) => img.is_featured) || categoryImages[0];

    return {
      id: category,
      title: category,
      coverImage: featuredImage.cloudinary_url || undefined,
      thumbnail: featuredImage.cloudinary_url || undefined,
      date:
        featuredImage.taken_date || featuredImage.upload_date
          ? new Date(
              featuredImage.taken_date || featuredImage.upload_date || ""
            ).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
            })
          : "",
      category: category,
      imageCount: categoryImages.length,
    };
  });
}

/**
 * Helper to fetch first image from gallery if no cover image
 */
async function getFirstImageForAlbum(albumId: string): Promise<string | undefined> {
  try {
    const imagesResponse = await getPublicAlbumImagesApiPublicAlbumsAlbumIdImagesGet({
      path: { album_id: albumId },
    });
    
    if (imagesResponse.data?.items && imagesResponse.data.items.length > 0) {
      return imagesResponse.data.items[0].cloudinary_url;
    }
  } catch (error) {
    console.error(`Failed to fetch first image for album ${albumId}:`, error);
  }
  return undefined;
}

/**
 * Hook for fetching featured galleries (public, no auth required)
 * Uses albums API (preferred), falls back to images API
 */
export function useFeaturedGalleries() {
  return useQuery({
    queryKey: ["galleries", "featured", "albums"],
    queryFn: async () => {
      // Try featured albums API first
      try {
        const featuredResponse =
          await getFeaturedPublicAlbumsApiPublicAlbumsFeaturedGet({
            query: { limit: 6 },
          });

        if (
          featuredResponse.data &&
          featuredResponse.data.items &&
          featuredResponse.data.items.length > 0
        ) {
          console.log(
            "Featured albums found:",
            featuredResponse.data.items.length
          );
          const galleries = transformAlbumsToGalleries(featuredResponse.data.items);
          
          // Fetch first image for galleries without cover images
          const galleriesWithCoverImages = await Promise.all(
            galleries.map(async (gallery) => {
              if (!gallery.coverImage) {
                const firstImageUrl = await getFirstImageForAlbum(gallery.id);
                return {
                  ...gallery,
                  coverImage: firstImageUrl,
                  thumbnail: firstImageUrl,
                };
              }
              return gallery;
            })
          );
          
          return galleriesWithCoverImages;
        }

        // If no featured albums, fall back to all public albums (limit to 6)
        console.log("No featured albums found, fetching all public albums...");
        const allAlbumsResponse = await getPublicAlbumsApiPublicAlbumsGet({
          query: { page: 1, page_size: 6 },
        });

        if (
          allAlbumsResponse.data &&
          allAlbumsResponse.data.items &&
          allAlbumsResponse.data.items.length > 0
        ) {
          console.log(
            "Public albums found:",
            allAlbumsResponse.data.items.length
          );
          const galleries = transformAlbumsToGalleries(allAlbumsResponse.data.items);
          
          // Fetch first image for galleries without cover images
          const galleriesWithCoverImages = await Promise.all(
            galleries.map(async (gallery) => {
              if (!gallery.coverImage) {
                const firstImageUrl = await getFirstImageForAlbum(gallery.id);
                return {
                  ...gallery,
                  coverImage: firstImageUrl,
                  thumbnail: firstImageUrl,
                };
              }
              return gallery;
            })
          );
          
          return galleriesWithCoverImages;
        }

        console.warn("No albums found in database");
        return [];
      } catch (error) {
        console.error("Failed to fetch featured galleries:", error);

        // Fallback to images API only if albums API fails completely
        try {
          const response =
            await getFeaturedPublicImagesApiPublicGalleryFeaturedGet();

          if (response.data && response.data.items) {
            console.log("Falling back to featured images API");
            return transformImagesToGalleries(response.data.items);
          }
        } catch (imagesError) {
          console.error(
            "Failed to fetch featured galleries from images API:",
            imagesError
          );
        }

        return [];
      }
    },
    staleTime: 1000 * 60 * 10, // 10 minutes (galleries don't change often)
  });
}

/**
 * Hook for fetching all public galleries
 */
export function usePublicGalleries() {
  return useQuery({
    queryKey: ["galleries", "public"],
    queryFn: async () => {
      try {
        const response = await getPublicAlbumsApiPublicAlbumsGet({
          query: {
            page: 1,
            page_size: 100, // Get all public galleries
          },
        });

        console.log("Galleries API response:", response);

        // Check for error property (exists when responseStyle is 'fields')
        if ("error" in response && response.error) {
          console.error("API error:", response.error);
          throw new Error(
            `Failed to fetch galleries: ${JSON.stringify(response.error)}`
          );
        }

        if (!response.data) {
          console.error("No data in response:", response);
          throw new Error("Failed to fetch galleries: No data in response");
        }

        const galleries = transformAlbumsToGalleries(response.data.items || []);
        
        // Fetch first image for galleries without cover images
        const galleriesWithCoverImages = await Promise.all(
          galleries.map(async (gallery) => {
            if (!gallery.coverImage) {
              const firstImageUrl = await getFirstImageForAlbum(gallery.id);
              return {
                ...gallery,
                coverImage: firstImageUrl,
                thumbnail: firstImageUrl,
              };
            }
            return gallery;
          })
        );
        
        return galleriesWithCoverImages;
      } catch (error) {
        console.error("Error fetching galleries:", error);

        // Provide more detailed error messages
        if (error instanceof TypeError && error.message.includes("fetch")) {
          throw new Error(
            `Network error: Cannot connect to API. ` +
              `Please ensure the backend is running and accessible. ` +
              `If accessing from mobile, make sure both devices are on the same WiFi network.`
          );
        }

        if (error instanceof Error) {
          throw error;
        }

        throw new Error(`Failed to fetch galleries: ${String(error)}`);
      }
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook for fetching a single gallery by ID
 */
export function useGallery(galleryId: string | undefined) {
  return useQuery({
    queryKey: ["gallery", galleryId],
    queryFn: async () => {
      if (!galleryId) {
        throw new Error("Gallery ID is required");
      }

      // Fetch album
      const albumResponse = await getPublicAlbumApiPublicAlbumsAlbumIdGet({
        path: { album_id: galleryId },
      });

      if (!albumResponse.data) {
        throw new Error("Failed to fetch gallery");
      }

      const album = albumResponse.data;

      // Fetch images for this album
      const imagesResponse =
        await getPublicAlbumImagesApiPublicAlbumsAlbumIdImagesGet({
          path: { album_id: galleryId },
        });

      let images: Array<{
        id: string;
        url: string;
        caption?: string;
        width?: number;
        height?: number;
      }> = [];

      if (imagesResponse.data) {
        // Filter out the cover image from the gallery view
        const coverImageId = album.cover_image_id;
        images = (imagesResponse.data.items || [])
          .filter((img: ImageResponse) => {
            // Exclude cover image from gallery view
            // cover_image_id stores the image's UUID id
            return img.id !== coverImageId;
          })
          .map((img: ImageResponse) => ({
            id: img.id || img.cloudinary_public_id || `img-${Math.random()}`,
            url: img.cloudinary_url,
            caption: img.title || img.description || img.alt_text || "",
            width: img.width,
            height: img.height,
          }));
      }

      // Fetch all public albums for navigation
      const allAlbumsResponse = await getPublicAlbumsApiPublicAlbumsGet({
        query: { page: 1, page_size: 100 },
      });

      const allAlbums: AlbumResponse[] = allAlbumsResponse.data?.items || [];

      // Find previous and next galleries
      const currentIndex = allAlbums.findIndex((a) => a.id === galleryId);
      const previousGallery =
        currentIndex > 0
          ? transformAlbumsToGalleries([allAlbums[currentIndex - 1]])[0]
          : undefined;
      const nextGallery =
        currentIndex >= 0 && currentIndex < allAlbums.length - 1
          ? transformAlbumsToGalleries([allAlbums[currentIndex + 1]])[0]
          : undefined;

      return {
        id: album.id,
        title: album.title,
        category: album.slug,
        date: new Date(album.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
        }),
        description: album.description || "",
        images: images,
        previousGallery: previousGallery,
        nextGallery: nextGallery,
      };
    },
    enabled: !!galleryId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook for fetching galleries in admin context (requires auth)
 */
export function useAdminGalleries() {
  const { accessToken } = getTokens();

  return useQuery({
    queryKey: ["galleries", "admin"],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("No access token found");
      }

      // For now, fetch public galleries (admin can see all)
      const response = await getPublicGalleryApiPublicGalleryGet();

      if (!response.data) {
        throw new Error("Failed to fetch galleries");
      }

      return transformImagesToGalleries(response.data.items);
    },
    enabled: !!accessToken,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
